/**
 * ChangeOrderDrawer Component
 * Create/Edit/View Change Order form for Purchase Orders
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  Plus,
  RefreshCw,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm, Resolver } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetCostCodesQuery } from '../../services/costCodeApi';
import {
  useCreateChangeOrderMutation,
  useUpdateChangeOrderMutation,
  useSubmitChangeOrderMutation,
  useApproveChangeOrderMutation,
  useRejectChangeOrderMutation,
} from '../../services/purchaseOrderApi';
import type {
  PurchaseOrder,
  ChangeOrder,
  ChangeOrderType,
  CreateChangeOrderRequest,
  UpdateChangeOrderRequest,
} from '../../types/purchaseOrder';
import type { CostCode } from '../../types/costCode';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  Input,
  NumberInput,
  SearchableDropdown,
  TextArea,
} from '../forms';

// Props interface
export interface ChangeOrderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  changeOrder?: ChangeOrder | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// Line item form data
interface LineItemFormData {
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  costCodeId: string;
  notes: string;
}

// Form data type
interface ChangeOrderFormData {
  title: string;
  description: string;
  reason: string;
  type: ChangeOrderType;
  requestedDate: string;
  lineItems: LineItemFormData[];
  notes: string;
  internalNotes: string;
}

// Type options
const TYPE_OPTIONS = [
  { value: 'addition', label: 'Addition' },
  { value: 'deduction', label: 'Deduction' },
  { value: 'modification', label: 'Modification' },
  { value: 'substitution', label: 'Substitution' },
];

// Unit options
const UNIT_OPTIONS = [
  { value: 'ea', label: 'Each' },
  { value: 'lf', label: 'Linear Feet' },
  { value: 'sf', label: 'Square Feet' },
  { value: 'cy', label: 'Cubic Yards' },
  { value: 'hrs', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'lot', label: 'Lot/Lump Sum' },
];

// Validation schema
const changeOrderSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').max(200, 'Max 200 characters'),
  description: Yup.string().required('Description is required').max(2000, 'Max 2000 characters'),
  reason: Yup.string().required('Reason is required').max(1000, 'Max 1000 characters'),
  type: Yup.string().oneOf(['addition', 'deduction', 'modification', 'substitution']).required(),
  requestedDate: Yup.string().required('Requested date is required'),
  lineItems: Yup.array().of(
    Yup.object().shape({
      description: Yup.string().required('Description required'),
      quantity: Yup.number().required('Quantity required'),
      unit: Yup.string().required('Unit required'),
      unitCost: Yup.number().required('Unit cost required'),
      costCodeId: Yup.string(),
      notes: Yup.string(),
    })
  ),
  notes: Yup.string().max(2000, 'Max 2000 characters'),
  internalNotes: Yup.string().max(2000, 'Max 2000 characters'),
});

export const ChangeOrderDrawer: React.FC<ChangeOrderDrawerProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  changeOrder,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    lineItems: true,
    notes: false,
  });
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const { data: costCodesData } = useGetCostCodesQuery({ isActive: true });
  const costCodes = costCodesData?.data?.costCodes || [];

  const [createChangeOrder, { isLoading: isCreating }] = useCreateChangeOrderMutation();
  const [updateChangeOrder, { isLoading: isUpdating }] = useUpdateChangeOrderMutation();
  const [submitChangeOrder, { isLoading: isSubmitting }] = useSubmitChangeOrderMutation();
  const [approveChangeOrder, { isLoading: isApproving }] = useApproveChangeOrderMutation();
  const [rejectChangeOrder, { isLoading: isRejecting }] = useRejectChangeOrderMutation();

  const isReadOnly = mode === 'view';
  const isLoading = isCreating || isUpdating || isSubmitting || isApproving || isRejecting;

  // Cost code options
  const costCodeOptions = useMemo(() => {
    return costCodes.map((cc: CostCode) => ({
      value: cc._id,
      label: `${cc.code} - ${cc.description}`,
    }));
  }, [costCodes]);

  // Default form values
  const defaultValues: ChangeOrderFormData = useMemo(() => {
    if (changeOrder) {
      return {
        title: changeOrder.title,
        description: changeOrder.description,
        reason: changeOrder.reason,
        type: changeOrder.type,
        requestedDate:
          changeOrder.requestedDate?.split('T')[0] || new Date().toISOString().split('T')[0],
        lineItems: changeOrder.lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitCost: item.unitCost,
          costCodeId: item.costCodeId || '',
          notes: item.notes || '',
        })),
        notes: changeOrder.notes || '',
        internalNotes: changeOrder.internalNotes || '',
      };
    }

    return {
      title: '',
      description: '',
      reason: '',
      type: 'addition' as ChangeOrderType,
      requestedDate: new Date().toISOString().split('T')[0],
      lineItems: [],
      notes: '',
      internalNotes: '',
    };
  }, [changeOrder]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<ChangeOrderFormData>({
    resolver: yupResolver(changeOrderSchema) as Resolver<ChangeOrderFormData>,
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const watchedLineItems = watch('lineItems');

  // Calculate total
  const totalAmount = useMemo(() => {
    return (watchedLineItems || []).reduce((sum, item) => {
      return sum + (item.quantity || 0) * (item.unitCost || 0);
    }, 0);
  }, [watchedLineItems]);

  // Reset form when change order changes
  useEffect(() => {
    reset(defaultValues);
  }, [changeOrder, defaultValues, reset]);

  // Handle form submission
  const onSubmit = async (data: ChangeOrderFormData) => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    try {
      const requestData: CreateChangeOrderRequest | UpdateChangeOrderRequest = {
        title: data.title,
        description: data.description,
        reason: data.reason,
        type: data.type,
        requestedDate: data.requestedDate,
        lineItems: data.lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitCost: item.unitCost,
          costCodeId: item.costCodeId || undefined,
          notes: item.notes || undefined,
        })),
        notes: data.notes || undefined,
        internalNotes: data.internalNotes || undefined,
      };

      if (mode === 'create') {
        await createChangeOrder({
          projectId,
          purchaseOrderId: purchaseOrder._id,
          data: requestData as CreateChangeOrderRequest,
        }).unwrap();
        toast.success('Change order created successfully');
      } else if (mode === 'edit' && changeOrder) {
        await updateChangeOrder({
          projectId,
          purchaseOrderId: purchaseOrder._id,
          changeOrderId: changeOrder._id,
          data: requestData as UpdateChangeOrderRequest,
        }).unwrap();
        toast.success('Change order updated successfully');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Change order save error:', error);
      toast.error('Failed to save change order');
    }
  };

  // Handle submit for approval
  const handleSubmitForApproval = async () => {
    if (!projectId || !changeOrder) return;

    try {
      await submitChangeOrder({
        projectId,
        purchaseOrderId: purchaseOrder._id,
        changeOrderId: changeOrder._id,
      }).unwrap();
      toast.success('Change order submitted for approval');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Submit for approval error:', error);
      toast.error('Failed to submit for approval');
    }
  };

  // Handle approve
  const handleApprove = async () => {
    if (!projectId || !changeOrder) return;

    try {
      await approveChangeOrder({
        projectId,
        purchaseOrderId: purchaseOrder._id,
        changeOrderId: changeOrder._id,
      }).unwrap();
      toast.success('Change order approved');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve change order');
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!projectId || !changeOrder || !rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await rejectChangeOrder({
        projectId,
        purchaseOrderId: purchaseOrder._id,
        changeOrderId: changeOrder._id,
        data: { rejectionReason: rejectReason },
      }).unwrap();
      toast.success('Change order rejected');
      setShowRejectModal(false);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject change order');
    }
  };

  // Add line item
  const addLineItem = () => {
    append({
      description: '',
      quantity: 1,
      unit: 'ea',
      unitCost: 0,
      costCodeId: '',
      notes: '',
    });
  };

  // Toggle section
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[70%] min-w-[700px] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <RefreshCw className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create'
                    ? 'Create Change Order'
                    : mode === 'edit'
                      ? 'Edit Change Order'
                      : `Change Order ${changeOrder?.changeOrderNumber || ''}`}
                </h2>
                <p className="text-sm text-gray-500">PO: {purchaseOrder.poNumber}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {changeOrder && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(changeOrder.status)}`}
                >
                  {changeOrder.status.charAt(0).toUpperCase() + changeOrder.status.slice(1)}
                </span>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Amount Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Change Order Amount</span>
                  <span
                    className={`text-2xl font-bold ${totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {totalAmount >= 0 ? '+' : ''}$
                    {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* Details Section */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('details')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Details</span>
                  </div>
                  {expandedSections.details ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.details && (
                  <div className="p-4 space-y-4">
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Title"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.title?.message}
                          disabled={isReadOnly}
                          required
                          placeholder="Brief title for the change order"
                        />
                      )}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            label="Type"
                            options={TYPE_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.type?.message}
                            disabled={isReadOnly}
                            required
                          />
                        )}
                      />
                      <Controller
                        name="requestedDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Requested Date"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.requestedDate?.message}
                            disabled={isReadOnly}
                            required
                          />
                        )}
                      />
                    </div>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Description"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.description?.message}
                          disabled={isReadOnly}
                          required
                          rows={3}
                          placeholder="Detailed description of the change"
                        />
                      )}
                    />
                    <Controller
                      name="reason"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Reason for Change"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.reason?.message}
                          disabled={isReadOnly}
                          required
                          rows={2}
                          placeholder="Why is this change necessary?"
                        />
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Line Items Section */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('lineItems')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Line Items</span>
                    <span className="text-sm text-gray-500">({fields.length} items)</span>
                  </div>
                  {expandedSections.lineItems ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.lineItems && (
                  <div className="p-4 space-y-4">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="p-4 bg-gray-50 rounded-lg space-y-3 border border-gray-200"
                      >
                        <div className="flex justify-between items-start">
                          <span className="text-sm font-medium text-gray-500">
                            Item {index + 1}
                          </span>
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <Controller
                          name={`lineItems.${index}.description`}
                          control={control}
                          render={({ field: inputField }) => (
                            <Input
                              label="Description"
                              value={inputField.value}
                              onChange={inputField.onChange}
                              error={errors.lineItems?.[index]?.description?.message}
                              disabled={isReadOnly}
                              required
                              placeholder="Item description"
                            />
                          )}
                        />
                        <div className="grid grid-cols-4 gap-3">
                          <Controller
                            name={`lineItems.${index}.quantity`}
                            control={control}
                            render={({ field: inputField }) => (
                              <NumberInput
                                label="Quantity"
                                value={inputField.value}
                                onChange={inputField.onChange}
                                error={errors.lineItems?.[index]?.quantity?.message}
                                disabled={isReadOnly}
                              />
                            )}
                          />
                          <Controller
                            name={`lineItems.${index}.unit`}
                            control={control}
                            render={({ field: selectField }) => (
                              <Dropdown
                                label="Unit"
                                options={UNIT_OPTIONS}
                                value={selectField.value}
                                onChange={selectField.onChange}
                                disabled={isReadOnly}
                              />
                            )}
                          />
                          <Controller
                            name={`lineItems.${index}.unitCost`}
                            control={control}
                            render={({ field: inputField }) => (
                              <CurrencyInput
                                label="Unit Cost"
                                value={inputField.value}
                                onChange={inputField.onChange}
                                error={errors.lineItems?.[index]?.unitCost?.message}
                                disabled={isReadOnly}
                              />
                            )}
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Amount
                            </label>
                            <div className="h-10 flex items-center px-3 bg-gray-100 rounded-lg text-gray-700 font-medium">
                              $
                              {(
                                (watchedLineItems?.[index]?.quantity || 0) *
                                (watchedLineItems?.[index]?.unitCost || 0)
                              ).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Controller
                            name={`lineItems.${index}.costCodeId`}
                            control={control}
                            render={({ field: selectField }) => (
                              <SearchableDropdown
                                label="Cost Code"
                                options={costCodeOptions}
                                value={selectField.value}
                                onChange={selectField.onChange}
                                disabled={isReadOnly}
                                placeholder="Select cost code"
                              />
                            )}
                          />
                          <Controller
                            name={`lineItems.${index}.notes`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Input
                                label="Notes"
                                value={inputField.value}
                                onChange={inputField.onChange}
                                disabled={isReadOnly}
                                placeholder="Optional notes"
                              />
                            )}
                          />
                        </div>
                      </div>
                    ))}

                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={addLineItem}
                        className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-brand-500 hover:text-brand-600 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Line Item</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('notes')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Notes</span>
                  </div>
                  {expandedSections.notes ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.notes && (
                  <div className="p-4 space-y-4">
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Notes"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.notes?.message}
                          disabled={isReadOnly}
                          rows={3}
                          placeholder="External notes (visible to all)"
                        />
                      )}
                    />
                    <Controller
                      name="internalNotes"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Internal Notes"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.internalNotes?.message}
                          disabled={isReadOnly}
                          rows={3}
                          placeholder="Internal notes (not visible to vendor)"
                        />
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Rejection reason display */}
              {changeOrder?.status === 'rejected' && changeOrder.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800">Rejection Reason</h4>
                      <p className="text-sm text-red-700 mt-1">{changeOrder.rejectionReason}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-100 px-8 py-5 bg-gray-50 flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            <div className="flex items-center space-x-3">
              {/* Workflow actions */}
              {mode === 'view' && changeOrder?.status === 'draft' && (
                <button
                  type="button"
                  onClick={handleSubmitForApproval}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <span>Submit for Approval</span>
                </button>
              )}
              {mode === 'view' && changeOrder?.status === 'pending' && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowRejectModal(true)}
                    disabled={isLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    <span>Reject</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={isLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" />
                    <span>Approve</span>
                  </button>
                </>
              )}
              {!isReadOnly && (
                <button
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading || !isDirty}
                  className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? 'Saving...'
                    : mode === 'create'
                      ? 'Create Change Order'
                      : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Change Order</h3>
            <TextArea
              label="Rejection Reason"
              value={rejectReason}
              onChange={setRejectReason}
              rows={4}
              required
              placeholder="Please provide a reason for rejecting this change order"
            />
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={!rejectReason.trim() || isRejecting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isRejecting ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ChangeOrderDrawer;
